import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Animated, Pressable, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

type InAppBannerKind = 'success' | 'info' | 'warning' | 'error';

interface InAppBannerInput {
  title: string;
  message?: string;
  kind?: InAppBannerKind;
  durationMs?: number;
}

interface ActiveInAppBanner {
  id: number;
  title: string;
  message?: string;
  kind: InAppBannerKind;
  durationMs: number;
}

interface InAppBannerContextValue {
  showBanner: (input: InAppBannerInput) => void;
  dismissBanner: () => void;
}

const DEFAULT_DURATION_MS = 6500;
const InAppBannerContext = createContext<InAppBannerContextValue | null>(null);

const BANNER_THEME: Record<
  InAppBannerKind,
  {
    icon: React.ComponentProps<typeof Ionicons>['name'];
    iconColor: string;
    containerClassName: string;
    titleClassName: string;
    messageClassName: string;
  }
> = {
  success: {
    icon: 'checkmark-circle',
    iconColor: '#2E8B57',
    containerClassName: 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-gray-900',
    titleClassName: 'text-green-900 dark:text-green-300',
    messageClassName: 'text-green-800 dark:text-gray-200',
  },
  info: {
    icon: 'information-circle',
    iconColor: '#2563eb',
    containerClassName: 'border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-gray-900',
    titleClassName: 'text-blue-900 dark:text-blue-300',
    messageClassName: 'text-blue-800 dark:text-gray-200',
  },
  warning: {
    icon: 'alert-circle',
    iconColor: '#b45309',
    containerClassName: 'border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-gray-900',
    titleClassName: 'text-amber-900 dark:text-amber-300',
    messageClassName: 'text-amber-800 dark:text-gray-200',
  },
  error: {
    icon: 'alert-circle',
    iconColor: '#dc2626',
    containerClassName: 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-gray-900',
    titleClassName: 'text-red-900 dark:text-red-300',
    messageClassName: 'text-red-800 dark:text-gray-200',
  },
};

export function InAppBannerProvider({ children }: { children: React.ReactNode }) {
  const [banner, setBanner] = useState<ActiveInAppBanner | null>(null);

  const showBanner = useCallback((input: InAppBannerInput) => {
    setBanner({
      id: Date.now(),
      title: input.title,
      kind: input.kind ?? 'info',
      durationMs: input.durationMs ?? DEFAULT_DURATION_MS,
      ...(input.message ? { message: input.message } : {}),
    });
  }, []);

  const dismissBanner = useCallback(() => {
    setBanner(null);
  }, []);

  const value = useMemo(
    () => ({ showBanner, dismissBanner }),
    [dismissBanner, showBanner],
  );

  return (
    <InAppBannerContext.Provider value={value}>
      {children}
      <InAppBannerHost banner={banner} onDismiss={dismissBanner} />
    </InAppBannerContext.Provider>
  );
}

function InAppBannerHost({
  banner,
  onDismiss,
}: {
  banner: ActiveInAppBanner | null;
  onDismiss: () => void;
}) {
  const insets = useSafeAreaInsets();
  const opacity = useRef(new Animated.Value(0)).current;

  const dismissWithAnimation = useCallback(() => {
    Animated.timing(opacity, {
      toValue: 0,
      duration: 180,
      useNativeDriver: true,
    }).start(onDismiss);
  }, [onDismiss, opacity]);

  useEffect(() => {
    if (!banner) return;

    opacity.setValue(0);
    Animated.timing(opacity, {
      toValue: 1,
      duration: 160,
      useNativeDriver: true,
    }).start();

    if (banner.durationMs <= 0) return;

    const timeout = setTimeout(dismissWithAnimation, banner.durationMs);
    return () => clearTimeout(timeout);
  }, [banner, dismissWithAnimation, opacity]);

  if (!banner) return null;

  const theme = BANNER_THEME[banner.kind];
  const translateY = opacity.interpolate({
    inputRange: [0, 1],
    outputRange: [-8, 0],
  });

  return (
    <View
      pointerEvents="box-none"
      className="absolute inset-x-4 z-50"
      style={{ top: insets.top + 12 }}
    >
      <Animated.View style={{ opacity, transform: [{ translateY }] }}>
        <View
          className={`mx-auto w-full max-w-xl rounded-2xl border px-4 py-3 shadow-card ${theme.containerClassName}`}
        >
          <View className="flex-row items-start gap-3">
            <Ionicons name={theme.icon} size={20} color={theme.iconColor} />
            <View className="flex-1">
              <Text className={`text-sm font-semibold ${theme.titleClassName}`}>
                {banner.title}
              </Text>
              {banner.message ? (
                <Text className={`mt-1 text-sm leading-5 ${theme.messageClassName}`}>
                  {banner.message}
                </Text>
              ) : null}
            </View>
            <Pressable onPress={dismissWithAnimation} hitSlop={8} className="rounded-full p-0.5">
              <Ionicons name="close" size={16} color="#6b7280" />
            </Pressable>
          </View>
        </View>
      </Animated.View>
    </View>
  );
}

export function useInAppBanner() {
  const value = useContext(InAppBannerContext);
  if (!value) {
    throw new Error('useInAppBanner must be used within InAppBannerProvider');
  }

  return value;
}
